import * as React from "react";
import { Info } from "lucide-react";

/** Colour of a confidence score: sure, fairly sure (still used), or below the review line. */
export function confidenceTone(value: number): { bar: string; text: string; word: string } {
  if (value >= 0.85) return { bar: "bg-emerald-500", text: "text-emerald-700", word: "High" };
  if (value >= 0.7) return { bar: "bg-amber-500", text: "text-amber-700", word: "Medium" };
  return { bar: "bg-rose-500", text: "text-rose-700", word: "Low" };
}

export function confidencePercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/**
 * A confidence score as a percentage and a bar, with an info button that
 * opens a panel explaining how the score was worked out (`basis`, one line
 * per factor).
 */
export function ConfidenceMeter({ label, value, basis, note }: { label: string; value: number; basis: string[]; note?: string }) {
  const [open, setOpen] = React.useState(false);
  const t = confidenceTone(value);
  const panelId = React.useId();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-medium text-zinc-600">{label}</span>
        <div
          className="h-1.5 w-24 overflow-hidden rounded-full bg-zinc-200"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(value * 100)}
          aria-label={label}
        >
          <div className={`h-full rounded-full ${t.bar}`} style={{ width: `${Math.max(2, Math.min(100, value * 100))}%` }} />
        </div>
        <span className={`font-semibold tabular-nums ${t.text}`}>
          {confidencePercent(value)} <span className="font-normal">({t.word})</span>
        </span>
        {note ? <span className="text-zinc-500">({note})</span> : null}
        {basis.length ? (
          <button
            type="button"
            className={`inline-flex items-center rounded p-0.5 hover:bg-zinc-200 hover:text-zinc-900 ${open ? "text-sky-700" : "text-zinc-500"}`}
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={open ? "Hide how this score is worked out" : "How this score is worked out"}
            title={open ? "Hide how this score is worked out" : "How this score is worked out"}
            onClick={() => setOpen((v) => !v)}
          >
            <Info className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
      {open ? (
        <div id={panelId} className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-950">
          <ul className="list-disc space-y-0.5 pl-4">
            {basis.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
